/**
 * "Learn from conversations" section of the Flux Agent memory dialog: the consent switch with a
 * plain explanation of what is stored, the latest energetic summary as 18 bars, and every stored
 * insight grouped by kind with delete and delete-all. Learning is on by default under the Terms
 * of Use; turning it off stops future learning but keeps what is stored until deleted.
 *
 * @module apps/web/src/components/flux-agent/learning/FluxAgentLearningPanel
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  fluxAgentLearningApi,
  type FluxAgentInsight,
  type FluxAgentInsightKind,
  type FluxAgentLearningState,
} from '@/lib/fluxAgentLearningApi';
import { cn } from '@/lib/utils';
import { FluxAgentSignalSummaryBars } from './FluxAgentSignalSummaryBars';

/** Section heading per kind, in display order. */
const KIND_LABELS: [FluxAgentInsightKind, string][] = [
  ['observation', 'Energy readings'],
  ['feature_prior', 'What seems to drive your energy'],
  ['field_suggestion', 'Suggested fields'],
  ['data_request', 'Questions it wants to ask'],
  ['signal_summary', 'Energetic summaries'],
  ['prosody', 'Voice (numbers only)'],
];

/** Human words for an insight's status, shown when it is no longer active. */
const STATUS_LABELS: Record<string, string> = {
  dismissed: 'Dismissed',
  applied: 'Added',
  answered: 'Answered',
};

/** Format a 0-10 axis value, or null when absent. */
function axis(label: string, value: number | null): string | null {
  return value === null || value === undefined ? null : `${label} ${Math.round(value)}/10`;
}

/** Title and detail lines shown for one insight. */
export interface InsightSummary {
  /** Short heading, such as the observation time or the field name. */
  title: string;
  /** One supporting line: values, rationale, or the question to ask. */
  detail: string;
}

/** One-line description of an insight for the list. */
export function describeInsight(insight: FluxAgentInsight): InsightSummary {
  switch (insight.kind) {
    case 'observation': {
      const when = new Date(insight.payload.at);
      const time = Number.isNaN(when.getTime())
        ? insight.payload.at
        : when.toLocaleString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });
      const axes = [
        axis('Productivity', insight.payload.productivity),
        axis('Rejuvenation', insight.payload.rejuvenation),
        axis('Resistance', insight.payload.resistance),
      ].filter(Boolean);
      return {
        title: `${time}${axes.length ? ` · ${axes.join(', ')}` : ''}`,
        detail: `${insight.payload.basis} (confidence ${Math.round(insight.confidence * 100)}%)`,
      };
    }
    case 'feature_prior': {
      const feature = insight.payload.feature
        .replace(/^(signal|custom)\./, '')
        .replace(/[._]/g, ' ');
      const effect =
        insight.payload.direction > 0
          ? 'raises'
          : insight.payload.direction < 0
            ? 'lowers'
            : 'affects';
      return {
        title: `More ${feature} ${effect} ${insight.payload.axis}`,
        detail: `Strength ${Math.round(insight.payload.strength * 100)}%, confidence ${Math.round(insight.confidence * 100)}%`,
      };
    }
    case 'field_suggestion':
      return { title: insight.payload.name, detail: insight.payload.question };
    case 'data_request':
      return { title: insight.payload.question, detail: insight.payload.reason };
    case 'signal_summary': {
      const when = new Date(insight.payload.at);
      const time = Number.isNaN(when.getTime())
        ? insight.payload.at
        : when.toLocaleString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });
      return {
        title: `${time} · ${insight.payload.toneLabel} · ${insight.payload.signals.length} of 18 signals`,
        detail: insight.payload.basis,
      };
    }
    case 'prosody': {
      const rate = insight.payload.wordsPerMinute;
      return {
        title: `${insight.payload.utterances} spoken messages, ${Math.round(insight.payload.speechSeconds)} s`,
        detail: `${rate === null ? '' : `${Math.round(rate)} words/min, `}pauses ${Math.round(insight.payload.pauseRatio * 100)}%`,
      };
    }
  }
}

/** Consent switch and grouped, deletable list of what Flux Agent learned from conversations. */
export function FluxAgentLearningPanel() {
  const [state, setState] = useState<FluxAgentLearningState | null>(null);
  const [insights, setInsights] = useState<FluxAgentInsight[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [learning, list] = await Promise.all([
      fluxAgentLearningApi.getLearning(),
      fluxAgentLearningApi.listInsights(),
    ]);
    setLoading(false);
    if (learning.ok) setState(learning.data);
    else setError(learning.message);
    if (list.ok) setInsights(list.data.insights);
    else setError(list.message);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async () => {
    if (!state) return;
    setSaving(true);
    setError(null);
    const result = await fluxAgentLearningApi.setLearning(!state.enabled);
    setSaving(false);
    if (result.ok) setState(result.data);
    else setError(result.message);
  };

  const remove = async (insight: FluxAgentInsight) => {
    const result = await fluxAgentLearningApi.deleteInsight(insight.id);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setInsights(current => current.filter(item => item.id !== insight.id));
  };

  const removeAll = async () => {
    if (
      !window.confirm(
        'Delete everything Flux Agent learned from your conversations? This can’t be undone.'
      )
    )
      return;
    const result = await fluxAgentLearningApi.deleteAllInsights();
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setInsights([]);
    void load();
  };

  const enabled = !!state?.enabled;
  const latestSummary = insights.find(
    (insight): insight is Extract<FluxAgentInsight, { kind: 'signal_summary' }> =>
      insight.kind === 'signal_summary'
  );

  return (
    <div className="space-y-4">
      <div className="bg-background/60 flex items-start gap-3 rounded-lg border p-3">
        <div className="min-w-0 flex-1 space-y-1">
          <p id="flux-learning-label" className="text-sm font-medium">
            Learn from conversations
          </p>
          <p id="flux-learning-description" className="text-muted-foreground text-xs">
            Learn from conversations: on by default under the Terms of Use; turn off anytime. After
            you chat, Flux Agent notes how your energy seemed, what seems to drive it, fields worth
            tracking, and questions worth asking later, and closes each conversation with a summary
            of 18 energy signals. In voice mode it also keeps a few numbers about how you spoke
            (pace, pauses, pitch), measured on your device; audio is never stored. These notes
            personalise your energy forecast. They are paraphrases, never quotes, and nothing about
            other people. Turning this off stops new learning but keeps what is stored; delete it
            below at any time.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-labelledby="flux-learning-label"
          aria-describedby="flux-learning-description"
          disabled={!state || saving}
          onClick={() => void toggle()}
          className={cn(
            'focus-visible:ring-ring relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50',
            enabled ? 'bg-primary' : 'bg-muted-foreground/30'
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'bg-background inline-block h-5 w-5 rounded-full shadow transition-transform',
              enabled ? 'translate-x-5' : 'translate-x-0.5'
            )}
          />
        </button>
      </div>

      {error && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
        >
          {error}
        </p>
      )}

      {loading && !insights.length ? (
        <p className="text-muted-foreground flex items-center gap-2 text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </p>
      ) : insights.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {enabled
            ? 'Nothing learned yet. Keep chatting and it will appear here.'
            : 'Nothing is stored.'}
        </p>
      ) : (
        <>
          {latestSummary && (
            <section aria-label="Latest energetic summary" className="space-y-1.5">
              <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                Latest energetic summary
              </h3>
              <FluxAgentSignalSummaryBars summary={latestSummary.payload} />
            </section>
          )}
          {KIND_LABELS.map(([kind, label]) => {
            const items = insights.filter(insight => insight.kind === kind);
            if (!items.length) return null;
            return (
              <section key={kind} aria-label={label} className="space-y-1.5">
                <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                  {label} ({items.length})
                </h3>
                <ul className="space-y-1.5">
                  {items.map(insight => {
                    const { title, detail } = describeInsight(insight);
                    return (
                      <li
                        key={insight.id}
                        className="flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="break-words font-medium">
                            {title}
                            {STATUS_LABELS[insight.status] && (
                              <span className="text-muted-foreground ml-2 text-xs font-normal">
                                {STATUS_LABELS[insight.status]}
                              </span>
                            )}
                          </p>
                          {detail && (
                            <p className="text-muted-foreground break-words text-xs">{detail}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 shrink-0 p-0"
                          onClick={() => void remove(insight)}
                          aria-label={`Delete “${title}”`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-destructive gap-1.5"
            onClick={() => void removeAll()}
          >
            <Trash2 className="h-4 w-4" /> Delete everything learned
          </Button>
        </>
      )}
    </div>
  );
}
